<template lang="html">
  <UHeader
    class="app-header"
    :toggle="false"
    :ui="{
      root: 'h-auto! top-0',
      container: 'gap-2 px-3 sm:gap-3 sm:px-6 h-(--ui-header-height) lg:px-4',
      // Theme default gives left/right `lg:flex-1` (centered title layout) — kill it
      // so the search center can claim the remaining width.
      left: 'flex shrink-0 items-center gap-2 sm:gap-3 lg:flex-none',
      center: 'flex min-w-0 flex-1',
      right: 'flex shrink-0 items-center justify-end gap-1.5 lg:flex-none',
      toggle: 'me-0!',
    }"
  >
    <template #top>
      <AppHeaderUtilityBar
        :is-compact="isCompact"
      />
    </template>

    <template #left>
      <a
        href="/"
        class="hidden shrink-0 lg:inline"
        aria-label="На главную"
      >
        <UIcon
          :name="brandIcons.logoSmall"
          class="h-10 w-auto"
        />
      </a>

      <UButton
        color="primary"
        class="app-header-catalog hidden shrink-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 lg:inline-flex"
        :trailing-icon="catalogOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        aria-haspopup="dialog"
        aria-controls="catalog-mega-menu-panel"
        :aria-expanded="catalogOpen"
        :class="{ 'app-header-catalog--open': catalogOpen }"
        @click="toggleCatalog"
      >
        Каталог
      </UButton>

      <UButton
        v-if="showBack"
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="tertiary"
        size="md"
        class="shrink-0 lg:hidden"
        aria-label="Назад"
        @click="goBack"
      />
    </template>

    <template #default>
      <HeaderSearch
        v-model:open="isSearchOpen"
        @close-without-restore="closeSearch({ restoreFocus: false })"
      />
    </template>

    <template #right>
      <AppHeaderPrimaryUtils />
    </template>
  </UHeader>

  <AppBottomNav />

  <ClientOnly>
    <CatalogMegaMenu v-if="catalogOpen" />
  </ClientOnly>
</template>

<script lang="ts" setup>
import AppBottomNav from './AppBottomNav.vue'
import AppHeaderPrimaryUtils from './AppHeaderPrimaryUtils.vue'
import AppHeaderUtilityBar from './AppHeaderUtilityBar.vue'
import { useHeaderChrome } from './composables/useHeaderChrome'

import CatalogMegaMenu from '~/app/AltApplication/Modules/Products/components/Catalog/MegaMenu/CatalogMegaMenu.vue'
import HeaderSearch from '~/app/AltApplication/Modules/Products/components/Search/HeaderSearch.vue'
import { useCatalogMenu } from '~/app/AltApplication/Modules/Products/composables/useCatalogMenu'
import { useSearchModal } from '~/app/AltApplication/Modules/Products/composables/useSearchModal'
import { brandIcons } from '~/app/theme/brand'

const {
  isOpen: searchModalOpenRef,
  open: openSearch,
  close: closeSearch,
} = useSearchModal()

const { isCompact, setFrozen } = useHeaderChrome()
const {
  isOpen: catalogOpenRef,
  toggle: toggleCatalog,
  close: closeCatalog,
} = useCatalogMenu()
const { showBack, goBack } = await useMobileBack()
const route = useRoute()

const catalogOpen = computed(() => catalogOpenRef.value)

watch(
  [catalogOpen, searchModalOpenRef],
  ([catalog, search]) => {
    setFrozen(Boolean(catalog || search))
  },
  { immediate: true },
)

watch(
  catalogOpen,
  (open) => {
    if (open && searchModalOpenRef.value) {
      closeSearch({ restoreFocus: false })
    }
  },
)

watch(
  searchModalOpenRef,
  (open) => {
    if (open && catalogOpenRef.value) {
      closeCatalog({ restoreFocus: false })
    }
  },
)

watch(
  () => route.fullPath,
  () => {
    if (searchModalOpenRef.value) {
      closeSearch({ restoreFocus: false })
    }
  },
)

const isSearchOpen = computed({
  get: () => searchModalOpenRef.value,
  set: (value: boolean) => {
    if (value) {
      openSearch()
    }
    else {
      closeSearch()
    }
  },
})
</script>
